import React from "react";
import { motion } from "framer-motion";
import { Stamp, GraduationCap, Briefcase, FileText, MessageSquare, ArrowRight, ShieldCheck } from "lucide-react";

const services = [
  {
    icon: Stamp,
    title: "US Tourist Visa",
    desc: "B1/B2 visitor visa filing with priority appointment scheduling and DS-160 expert review.",
    badge: "Most Popular",
    accent: "gold",
  },
  {
    icon: GraduationCap,
    title: "Student Visa",
    desc: "F-1, F-2 and exchange visitor (J-1) processing with SEVIS, I-20 & financial documentation.",
    accent: "navy",
  },
  {
    icon: Briefcase,
    title: "Business Visa",
    desc: "L-1, H-1B, E-2 and B1 business visitor processing — corporate letters & itinerary support.",
    accent: "sky",
  },
  {
    icon: FileText,
    title: "Documentation Assistance",
    desc: "Affidavits, sponsorship letters, financial proofs, translation & end-to-end paperwork.",
    accent: "gold",
  },
  {
    icon: MessageSquare,
    title: "Interview Preparation",
    desc: "1-on-1 mock interviews with ex-consular advisors. Personalised Q&A drilling.",
    accent: "navy",
  },
  {
    icon: ShieldCheck,
    title: "Approval Guarantee",
    desc: "Refund-eligible cases. Transparent eligibility audit before you spend a single rupee.",
    badge: "Premium",
    accent: "sky",
  },
];

const accentStyle = {
  navy: { bg: "bg-navy-deep/8", border: "border-navy-deep/20", text: "text-navy-deep", glow: "rgba(0,51,102,0.15)" },
  sky: { bg: "bg-sky-soft", border: "border-sky-brand/30", text: "text-sky-brand", glow: "rgba(0,153,204,0.18)" },
  gold: { bg: "bg-gold/10", border: "border-gold/30", text: "text-gold-dark", glow: "rgba(212,175,55,0.18)" },
};

export default function VisaServices() {
  return (
    <section id="visa" data-testid="visa-services-section" className="relative py-24 md:py-32 bg-white">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 rounded-full opacity-25" style={{ background: "radial-gradient(circle, #0099CC, transparent 70%)", filter: "blur(60px)" }} />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #D4AF37, transparent 70%)", filter: "blur(60px)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-dark font-semibold mb-4">— Visa Services</div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05] text-navy-deep">
            End-to-end <span className="text-sky-brand">US visa</span> &<br className="hidden md:block" /> global mobility services.
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
            Filed by consular ex-veterans. Reviewed by senior immigration counsel. Approved at a 98% success rate across categories.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            const style = accentStyle[s.accent];
            return (
              <motion.div
                key={s.title}
                data-testid={`visa-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.06, duration: 0.7 }}
                className="group relative tilt-card glow-border rounded-3xl p-8 bg-white border border-navy-deep/8 soft-shadow overflow-hidden"
              >
                {s.badge && (
                  <div className="absolute top-5 right-5 px-3 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold-dark text-[10px] uppercase tracking-[0.22em] font-bold">
                    {s.badge}
                  </div>
                )}

                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${style.bg} border ${style.border}`}
                  style={{ boxShadow: `0 6px 20px -8px ${style.glow}` }}
                >
                  <Icon className={`w-6 h-6 ${style.text}`} strokeWidth={2} />
                </div>

                <h3 className="font-heading text-2xl font-semibold tracking-tight mb-3 text-navy-deep">{s.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-6">{s.desc}</p>

                <button className="inline-flex items-center gap-2 text-sm font-semibold text-navy-deep group-hover:text-sky-brand transition-colors">
                  Learn more
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
